"use client"

import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, CheckCircle2, History, Loader2, RotateCcw, Search, Undo2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import MultiSelectFilterCombobox from "./MultiSelectFilterCombobox"
import { API_ENDPOINTS } from "../../../constants/config"
import toast from "react-hot-toast"
import Pagination from "../../Common/Pagination"

const TYPE_OPTIONS = [
  { key: "purchase", value: "purchase", label: "Purchase" },
  { key: "issue", value: "issue", label: "Issue" },
  { key: "return", value: "return", label: "Return" },
  { key: "adjustment", value: "adjustment", label: "Adjustment" },
  { key: "hold", value: "hold", label: "Hold" },
  { key: "unhold", value: "unhold", label: "Unhold" },
]

const TYPE_LABELS = {
  purchase: "Purchase",
  issue: "Issue",
  return: "Return",
  adjustment: "Adjustment",
  hold: "Hold",
  unhold: "Unhold",
}

// Only issue/purchase are reversible, and only within a 1-hour window —
// enforced server-side; this is purely for deciding whether to show the
// button and what countdown to display.
const UNDOABLE_TYPES = ["issue", "purchase"]
const UNDO_WINDOW_MS = 60 * 60 * 1000

function getUndoInfo(row, now) {
  if (row.deletedAt) return { canUndo: false, isUndone: true }
  if (!UNDOABLE_TYPES.includes(row.type)) return { canUndo: false }
  // Fully-returned issues have nothing left to reverse. A partial return is fine —
  // undo will only restore whatever's still outstanding (backend handles the math).
  if (row.type === "issue" && row.quantity - (Number(row.returned_quantity) || 0) <= 0) {
    return { canUndo: false }
  }
  const createdAt = new Date(row.created_at).getTime()
  if (Number.isNaN(createdAt)) return { canUndo: false }
  const remainingMs = createdAt + UNDO_WINDOW_MS - now
  if (remainingMs <= 0) return { canUndo: false }
  return { canUndo: true, remainingMs }
}

function getReturnInfo(row) {
  if (row.type !== "issue" || row.deletedAt) return { canReturn: false }
  const returned = Number(row.returned_quantity) || 0
  const remaining = Number((row.quantity - returned).toFixed(10))
  if (remaining <= 0) return { canReturn: false, fullyReturned: true, returned }
  return { canReturn: true, remaining, returned }
}

function formatRemaining(ms) {
  const totalMin = Math.max(1, Math.ceil(ms / 60000))
  if (totalMin >= 60) return `${Math.floor(totalMin / 60)}h ${totalMin % 60}m left`
  return `${totalMin}m left`
}

const rowTypeLabel = (row) => {
  const base = TYPE_LABELS[row.type] || row.type
  if (row.type === "adjustment" && row.adjustment_type) return `${base} (${row.adjustment_type})`
  return base
}

const partyFor = (row) => {
  const meta = row.metadata || {}
  return meta.customer_name || meta.issued_to || meta.vendor_name || null
}

const typeBadgeClass = (type) => {
  switch (type) {
    case "purchase":
      return "bg-emerald-100 text-emerald-800 border-emerald-200"
    case "issue":
      return "bg-blue-100 text-blue-800 border-blue-200"
    case "return":
      return "bg-violet-100 text-violet-800 border-violet-200"
    case "adjustment":
      return "bg-amber-100 text-amber-900 border-amber-200"
    case "hold":
      return "bg-orange-100 text-orange-900 border-orange-200"
    case "unhold":
      return "bg-slate-100 text-slate-800 border-slate-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}

function formatWhen(iso) {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    })
  } catch {
    return "—"
  }
}

// Sentinel for "don't attribute this return to any party" — always offered
// alongside real parties so nobody's forced to pick someone this return isn't for.
const NO_PARTY_VALUE = "__no_party__"
const NO_PARTY_OPTION = (remaining) => ({
  customer_id: null,
  isNone: true,
  name: null,
  outstanding: remaining,
})

export default function InventoryAllTransactions() {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [typeFilter, setTypeFilter] = useState([])
  const [limit, setLimit] = useState(50)
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [undoingId, setUndoingId] = useState(null)
  const [now, setNow] = useState(() => Date.now())
  // A single Return dialog, in one of two modes depending on whether the issue
  // it's opened from was linked to a real party (metadata.customer_id):
  //  - "simple": no party on record — return is tied to just this one transaction.
  //  - "party": party on record — shows everyone who's taken this item (deduped),
  //    defaulted to this row's own party but switchable, and returns against
  //    that party's total outstanding across all their issues of this item.
  const [returnTarget, setReturnTarget] = useState(null)
  const [returnQty, setReturnQty] = useState("")
  const [returnNotes, setReturnNotes] = useState("")
  const [returnSubmitting, setReturnSubmitting] = useState(false)
  const [parties, setParties] = useState([])
  const [partiesLoading, setPartiesLoading] = useState(false)
  const [selectedReturnParty, setSelectedReturnParty] = useState(null)

  // Ticks the countdown shown on each row — the actual undo eligibility is
  // always re-checked server-side when the button is clicked, this is just
  // so the "42m left" text counts down without a full refetch.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 350)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchTerm, typeFilter])

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Not signed in")
        return
      }
      const params = new URLSearchParams({ page: String(page), limit: String(limit), paginate: "true" })
      if (typeFilter.length) params.set("type", typeFilter.join(","))
      if (debouncedSearchTerm.trim()) params.set("search", debouncedSearchTerm.trim())
      const response = await fetch(`${API_ENDPOINTS.inventoryTransactions}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error("Failed to load transactions")
      const body = await response.json()
      setRows(body.data || [])
      setTotal(typeof body.count === "number" ? body.count : (body.data || []).length)
    } catch (e) {
      console.error(e)
      toast.error(e.message || "Failed to load transactions")
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [page, limit, typeFilter, debouncedSearchTerm])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  const handleUndo = async (row) => {
    const label = TYPE_LABELS[row.type]?.toLowerCase() || row.type
    if (!window.confirm(`Undo this ${label}? Stock will be restored${row.type === "issue" ? " and any linked ledger entry removed" : ""}.`)) {
      return
    }
    setUndoingId(row._id)
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(API_ENDPOINTS.inventoryTransactionUndo(row._id), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) throw new Error(data?.message || "Failed to undo transaction")
      toast.success("Transaction undone — stock restored")
      if (data?.partial_note) toast(data.partial_note, { icon: "ℹ️", duration: 6000 })
      if (data?.ledger_note) toast(data.ledger_note, { icon: "⚠️", duration: 6000 })
      fetchTransactions()
    } catch (e) {
      toast.error(e.message || "Failed to undo transaction")
    } finally {
      setUndoingId(null)
    }
  }

  const openReturn = async (row) => {
    const info = getReturnInfo(row)
    const customerId = row.metadata?.customer_id
    setReturnNotes("")
    setSelectedReturnParty(null)
    setParties([])
    // Tentatively open in party mode while we check — every issue of this item
    // (not just this row) can surface a real party here. If none exist at all,
    // this downgrades to "simple" below.
    setReturnTarget({ ...row, mode: "party", remaining: info.remaining })
    setReturnQty("")
    setPartiesLoading(true)
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(API_ENDPOINTS.inventoryItemParties(row.inventory_id), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error("Failed to load parties")
      const data = await response.json()
      const list = data.data || []
      setParties(list)
      if (list.length === 0) {
        // This item has never had a real party linked to any issue — nothing to
        // dedupe, just return against this one transaction.
        setReturnTarget({ ...row, mode: "simple", remaining: info.remaining })
        setReturnQty(String(info.remaining))
        return
      }
      // Default to this row's own party if it's a real one. Otherwise default to
      // "no party" — this row was never attributed to anyone, so don't force a pick;
      // staff can still switch to a real party from the dropdown if that's actually
      // who this is for.
      const ownParty = customerId ? list.find((p) => p.customer_id === customerId) : null
      if (ownParty) {
        setSelectedReturnParty(ownParty)
        setReturnQty(String(ownParty.outstanding))
      } else {
        setSelectedReturnParty(NO_PARTY_OPTION(info.remaining))
        setReturnQty(String(info.remaining))
      }
    } catch {
      toast.error("Failed to load parties for this item")
      setReturnTarget({ ...row, mode: "simple", remaining: info.remaining })
      setReturnQty(String(info.remaining))
    } finally {
      setPartiesLoading(false)
    }
  }

  const closeReturn = () => {
    if (returnSubmitting) return
    setReturnTarget(null)
    setReturnQty("")
    setReturnNotes("")
    setParties([])
    setSelectedReturnParty(null)
  }

  const selectReturnPartyById = (customerId) => {
    if (customerId === NO_PARTY_VALUE) {
      const opt = NO_PARTY_OPTION(returnTarget?.remaining ?? 0)
      setSelectedReturnParty(opt)
      setReturnQty(String(opt.outstanding))
      return
    }
    const party = parties.find((p) => p.customer_id === customerId)
    if (!party) return
    setSelectedReturnParty(party)
    setReturnQty(String(party.outstanding))
  }

  const handleReturnSubmit = async (e) => {
    e.preventDefault()
    const qty = Number.parseFloat(returnQty)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Enter a quantity greater than 0")
      return
    }

    const isPartyMode = returnTarget.mode === "party"
    if (isPartyMode && !selectedReturnParty) {
      toast.error("Select a party to return against")
      return
    }
    const usesRealParty = isPartyMode && !selectedReturnParty.isNone
    const cap = usesRealParty ? selectedReturnParty.outstanding : returnTarget.remaining
    if (qty > cap) {
      toast.error(usesRealParty ? `Only ${cap} outstanding with ${selectedReturnParty.name}` : `Only ${cap} left to return from this issue`)
      return
    }

    setReturnSubmitting(true)
    try {
      const token = localStorage.getItem("token")
      const url = usesRealParty
        ? API_ENDPOINTS.inventoryReturnToParty(returnTarget.inventory_id)
        : API_ENDPOINTS.inventoryTransactionReturn(returnTarget._id)
      const body = usesRealParty
        ? { customer_id: selectedReturnParty.customer_id, quantity: qty, notes: returnNotes }
        : { quantity: qty, notes: returnNotes }
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) throw new Error(data?.message || "Failed to return stock")
      toast.success(usesRealParty ? `${qty} returned by ${selectedReturnParty.name} — stock restored` : `${qty} returned — stock restored`)
      closeReturn()
      fetchTransactions()
    } catch (err) {
      toast.error(err.message || "Failed to return stock")
    } finally {
      setReturnSubmitting(false)
    }
  }

  return (
    <div className="w-full mx-auto max-w-[1600px] space-y-4 px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0 border-[#0263cc]/30 text-[#0263cc]"
            onClick={() => navigate("/dashboard/inventory")}
            aria-label="Back to inventory"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2 truncate">
              <History className="h-6 w-6 text-[#0263cc] shrink-0" />
              <span className="truncate">All inventory transactions</span>
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Stock movements across all items ({total} total{total !== rows.length ? ` · page ${page}` : ""})
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by item, category, brand, ID, notes…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full"
            />
          </div>
          <MultiSelectFilterCombobox
            placeholder="Type"
            allLabel="All types"
            value={typeFilter}
            onValueChange={setTypeFilter}
            options={TYPE_OPTIONS}
            className="w-full sm:w-[200px]"
          />
        </div>
      </div>

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 py-4">
          <CardTitle className="text-base sm:text-lg text-slate-800">Transaction log</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-500 gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading…</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-500 px-4">No transactions found for this filter.</div>
          ) : (
            <>
              {/* Desktop / tablet table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3 font-medium whitespace-nowrap">When</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Item</th>
                      <th className="px-4 py-3 font-medium">Party</th>
                      <th className="px-4 py-3 font-medium text-right whitespace-nowrap">ID</th>
                      <th className="px-4 py-3 font-medium text-right whitespace-nowrap">Qty</th>
                      <th className="px-4 py-3 font-medium text-right whitespace-nowrap">Old → New</th>
                      <th className="px-4 py-3 font-medium min-w-[140px]">Notes</th>
                      <th className="px-4 py-3 font-medium whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((row) => {
                      const item = row.inventory
                      const name = item?.item_name || "—"
                      const invId = row.inventory_id ?? item?.inventory_id
                      const undoInfo = getUndoInfo(row, now)
                      const returnInfo = getReturnInfo(row)
                      return (
                        <tr
                          key={row._id}
                          className="hover:bg-slate-50/80"
                        >
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatWhen(row.created_at)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${typeBadgeClass(row.type)}`}
                            >
                              {rowTypeLabel(row)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-900 max-w-[240px]">
                            <div className="truncate font-medium" title={name}>
                              {name}
                            </div>
                            {item?.category && (
                              <div className="text-xs text-slate-500 truncate">{item.category}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">{partyFor(row) || "—"}</td>
                          <td className="px-4 py-3 text-right font-mono text-slate-700">{invId}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-800">{row.quantity}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600 whitespace-nowrap">
                            {row.old_quantity} → {row.new_quantity}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs max-w-[200px] break-words">
                            {row.notes || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {undoInfo.canUndo ? (
                                <button
                                  type="button"
                                  disabled={undoingId === row._id}
                                  onClick={() => handleUndo(row)}
                                  className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                                >
                                  {undoingId === row._id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Undo2 className="h-3 w-3" />
                                  )}
                                  {formatRemaining(undoInfo.remainingMs)}
                                </button>
                              ) : undoInfo.isUndone ? (
                                <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Undone
                                </span>
                              ) : null}

                              {returnInfo.canReturn ? (
                                <button
                                  type="button"
                                  onClick={() => openReturn(row)}
                                  title={
                                    returnInfo.returned > 0
                                      ? `${returnInfo.returned} already returned · ${returnInfo.remaining} left`
                                      : undefined
                                  }
                                  className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100"
                                >
                                  <RotateCcw className="h-3 w-3" />
                                  Return
                                  {returnInfo.returned > 0 && (
                                    <span className="text-violet-500">({returnInfo.remaining} left)</span>
                                  )}
                                </button>
                              ) : returnInfo.fullyReturned ? (
                                <span className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Returned
                                </span>
                              ) : null}

                              {!undoInfo.canUndo && !undoInfo.isUndone && !returnInfo.canReturn && !returnInfo.fullyReturned && (
                                <span className="text-xs text-slate-300">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <ul className="md:hidden divide-y divide-slate-100">
                {rows.map((row) => {
                  const item = row.inventory
                  const name = item?.item_name || "—"
                  const invId = row.inventory_id ?? item?.inventory_id
                  const undoInfo = getUndoInfo(row, now)
                  const returnInfo = getReturnInfo(row)
                  return (
                    <li key={row._id} className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <span
                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${typeBadgeClass(row.type)}`}
                        >
                          {rowTypeLabel(row)}
                        </span>
                        <span className="text-xs text-slate-500 shrink-0">{formatWhen(row.created_at)}</span>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="text-left font-medium text-[#0263cc] text-base w-full"
                          onClick={() => navigate(`/dashboard/inventory/${invId}`)}
                        >
                          {name}
                        </button>
                        {item?.category && <div className="text-xs text-slate-500">{item.category}</div>}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500 text-xs block">Inventory ID</span>
                          <span className="font-mono">{invId}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs block">Quantity</span>
                          <span className="tabular-nums">{row.quantity}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-slate-500 text-xs block">Stock</span>
                          <span className="tabular-nums text-slate-700">
                            {row.old_quantity} → {row.new_quantity}
                          </span>
                        </div>
                        {partyFor(row) && (
                          <div className="col-span-2">
                            <span className="text-slate-500 text-xs block">Party</span>
                            <span className="text-slate-700">{partyFor(row)}</span>
                          </div>
                        )}
                        {row.notes ? (
                          <div className="col-span-2 text-xs text-slate-600 pt-1 border-t border-slate-100 mt-1">
                            {row.notes}
                          </div>
                        ) : null}
                      </div>
                      {undoInfo.canUndo && (
                        <button
                          type="button"
                          disabled={undoingId === row._id}
                          onClick={() => handleUndo(row)}
                          className="mt-1 inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 disabled:opacity-50"
                        >
                          {undoingId === row._id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Undo2 className="h-3 w-3" />
                          )}
                          Undo · {formatRemaining(undoInfo.remainingMs)}
                        </button>
                      )}
                      {undoInfo.isUndone && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-slate-600">
                          <CheckCircle2 className="h-3 w-3" />
                          Undone
                        </span>
                      )}
                      {returnInfo.canReturn && (
                        <button
                          type="button"
                          onClick={() => openReturn(row)}
                          className="mt-1 ml-1.5 inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-medium text-violet-700"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Return
                          {returnInfo.returned > 0 && (
                            <span className="text-violet-500">({returnInfo.remaining} left)</span>
                          )}
                        </button>
                      )}
                      {returnInfo.fullyReturned && (
                        <span className="mt-1 ml-1.5 inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-medium text-violet-700">
                          <CheckCircle2 className="h-3 w-3" />
                          Returned
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>

              <Pagination
                currentPage={page}
                totalItems={total}
                itemsPerPage={limit}
                onPageChange={setPage}
                onItemsPerPageChange={(v) => { setLimit(v); setPage(1) }}
                pageSizeOptions={[25, 50, 100]}
                itemLabel="transactions"
              />
            </>
          )}
        </CardContent>
      </Card>

      {returnTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeReturn}>
          <form
            onSubmit={handleReturnSubmit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl space-y-4"
          >
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 shrink-0">
                <RotateCcw className="h-4 w-4 text-violet-700" />
              </span>
              <h2 className="text-base font-semibold text-slate-900">Return stock</h2>
            </div>

            {returnTarget.mode === "simple" ? (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                {returnTarget.inventory?.item_name || "Item"} · issued {returnTarget.quantity}
                {returnTarget.metadata?.issued_to ? ` to ${returnTarget.metadata.issued_to}` : ""}
                {returnTarget.returned_quantity > 0 && ` · ${returnTarget.returned_quantity} already returned`}
              </p>
            ) : (
              <>
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Opened from the row: {returnTarget.quantity} issued
                  {partyFor(returnTarget) ? ` to ${partyFor(returnTarget)}` : ""}
                  {returnTarget.created_at ? ` on ${new Date(returnTarget.created_at).toLocaleDateString()}` : ""}
                </p>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Returned by</label>
                  {partiesLoading ? (
                    <p className="py-2 text-sm text-slate-400">Loading parties...</p>
                  ) : parties.length === 0 ? (
                    <p className="mt-1.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      Nobody has an outstanding issue of this item to return — every unit issued has already been
                      returned, or none has been issued yet.
                    </p>
                  ) : (
                    <Select
                      value={selectedReturnParty ? selectedReturnParty.customer_id || NO_PARTY_VALUE : ""}
                      onValueChange={selectReturnPartyById}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select who's returning it…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_PARTY_VALUE}>No party — just this transaction</SelectItem>
                        {parties.map((p) => (
                          <SelectItem key={p.customer_id} value={p.customer_id}>
                            {p.name} ({p.outstanding} issued)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </>
            )}

            {(returnTarget.mode === "simple" || selectedReturnParty) && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Quantity to return{" "}
                    {returnTarget.mode === "simple"
                      ? `(max ${returnTarget.remaining})`
                      : `(max ${selectedReturnParty.outstanding})`}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    max={returnTarget.mode === "simple" ? returnTarget.remaining : selectedReturnParty.outstanding}
                    value={returnQty}
                    onChange={(e) => setReturnQty(e.target.value)}
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Notes (optional)</label>
                  <Input
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    placeholder="Why is it coming back?"
                  />
                </div>

                {returnTarget.mode === "party" && !selectedReturnParty.isNone && (
                  <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <span>⚠️</span>
                    <span>
                      This will also post a return entry to <strong>{selectedReturnParty.name}</strong>&apos;s
                      customer ledger (amount not tracked yet).
                    </span>
                  </p>
                )}
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeReturn} disabled={returnSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-violet-600 hover:bg-violet-700"
                disabled={returnSubmitting || (returnTarget.mode === "party" && !selectedReturnParty)}
              >
                {returnSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm return"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
